import { Form, Input, Typography } from 'antd'
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChangePassword } from '../../api'
import CustomBreadCrumb from '../../components/breadCrumb/breadCrumb'
import { CustomButton } from '../../components/button/buttonCustom'
import { ButtonOutlinedCustom } from '../../components/button/buttonOulined'
import LoadingOverlay from '../../components/loading/loadingOverlay'
import {
  TOKEN_KEY,
  USER_DETAILS_KEY,
} from '../../components/providers/authProvider'
import { useNotification } from '../../components/providers/notification/useNotification'
import { setTitleHeader } from '../../store/use-title-header-store'
import {
  clearFieldErrorMessageOnChange,
  onlyLatinCharsDigitsSpecialSymbolsRule,
} from '../../utils/form.constants'
import useResponsive from '../../utils/hooks/useResponsive'
import { socketConnectNew } from '../../utils/socket'
import './change-password.css'

const breadCrumb = [
  { name: 'Portfolio', path: '/' },
  { name: 'Change Password', path: '/change-password' },
]

type ChangePasswordFormTypes = { newPassword: string; oldPassword: string }

export const ThemedChangePasswordPage: React.FC<any> = () => {
  const [form] = Form.useForm<ChangePasswordFormTypes>()
  const { isTablet } = useResponsive()
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  const { open: openNotification } = useNotification()

  const requiredFields = ['oldPassword', 'newPassword', 'confirmPassword']
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)

  const checkAllFieldsFilled = () => {
    const values = form.getFieldsValue(requiredFields as any)
    return requiredFields.every((field) => !!values[field])
  }

  const onChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setValue(value.toString())
    },
    []
  )

  const { mutate: changePassword, isLoading } = useChangePassword({
    onSuccess: (response: {
      data: { message: string; data: { access_token: string; details: any } }
    }) => {
      if (openNotification) {
        openNotification?.({
          key: 'change_password_success',
          message: 'Change password successfully!',
          type: 'success',
        })
      }
      localStorage.setItem(TOKEN_KEY, response.data.data['access_token'])
      localStorage.setItem(
        USER_DETAILS_KEY,
        JSON.stringify(response.data.data['details'])
      )
      localStorage.setItem(
        'status_user',
        response?.data?.data?.details?.worldCheckVerification?.status
      )
      socketConnectNew(response.data.data['access_token'])
      navigate('/')
    },
    onError: (error: any) => {
      if (openNotification) {
        openNotification({
          key: 'change_password_failure',
          message: error?.message || 'Change password failed!',
          type: 'error',
        })
      }
    },
  })

  const shouldValidate = value.length >= 6

  const onFinish = (values: ChangePasswordFormTypes) => {
    changePassword(values)
  }

  const handleKeyDown = (event: any) => {
    if (event.key === ' ') {
      event.preventDefault()
    }
  }

  useEffect(() => {
    setTitleHeader('Profile Settings')
  }, [])

  return (
    <div className='change-password-container'>
      <CustomBreadCrumb breadCrumb={breadCrumb} showHome={true} />
      <div className='change-password-content'>
        <Form
          style={{ width: '100%' }}
          form={form}
          onFinish={onFinish}
          validateTrigger='onBlur'
          onValuesChange={() => {
            clearFieldErrorMessageOnChange(form)
            setIsAllFieldsFilled(checkAllFieldsFilled())
          }}
          className='change-password-info'>
          <Typography className='change-password-title'>
            Change password
          </Typography>
          <Form.Item
            label='Current Password'
            name='oldPassword'
            validateTrigger='onBlur'
            rules={[
              onlyLatinCharsDigitsSpecialSymbolsRule,
              { required: true, message: 'This field is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  if (value.length >= 6) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('6 characters at least'))
                },
              },
            ]}
            initialValue=''
            hasFeedback={shouldValidate}
            className='input-password'>
            <Input.Password
              placeholder='Enter current password'
              size='large'
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
          <Form.Item
            label='New Password'
            name='newPassword'
            rules={[
              onlyLatinCharsDigitsSpecialSymbolsRule,
              { required: true, message: 'This field is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  if (value.length >= 6) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('6 characters at least'))
                },
              },
            ]}
            validateTrigger='onBlur'
            initialValue=''
            hasFeedback={shouldValidate}
            className='input-password'>
            <Input.Password
              onChange={onChange}
              placeholder='Enter new password (at least 6 characters, case sensitive)'
              size='large'
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
          <Form.Item
            label='Confirm Password'
            name='confirmPassword'
            dependencies={['newPassword']}
            hasFeedback={shouldValidate}
            rules={[
              onlyLatinCharsDigitsSpecialSymbolsRule,
              { required: true, message: 'This field is required' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) return Promise.resolve()
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(
                    new Error(
                      'The two passwords that you entered do not match!'
                    )
                  )
                },
              }),
            ]}
            validateTrigger='onBlur'
            className='input-password'>
            <Input.Password
              placeholder='Re-enter new password'
              size='large'
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              gap: '24px',
            }}>
            <ButtonOutlinedCustom
              isLoading={isLoading}
              text={'Cancel'}
              size={isTablet ? 'medium' : 'large'}
              width={'50%'}
              onClick={() => navigate('/home')}
            />
            <CustomButton
              isLoading={isLoading}
              type='submit'
              text={'Update'}
              size={isTablet ? 'medium' : 'large'}
              width={'50%'}
              disabled={!isAllFieldsFilled}
            />
          </div>
        </Form>
      </div>
      <LoadingOverlay loading={isLoading} />
    </div>
  )
}
